import flattenHierarchy from "backend/flattenHierarchy";
import useElement from "backend/hooks/elements/useElement";
import useHierarchy from "backend/hooks/elements/useHierarchy";
import useScorecardTopElement from "backend/hooks/model/useScorecardTopElement";
import ExecutiveDashboardToolbar from "components/toolbars/ExecutiveDashboardToolbar";
import ElementOverviewDonutCard from "design/components/cards/ElementOverviewDonutCard";
import { InitiativeCard } from "design/components/cards/InitiativeCard";
import { KPICard } from "design/components/cards/KPICard";
import OverallPerformanceCard from "design/components/cards/OverallPerformanceCard";
import { IChartSelectEvent } from "design/components/charts/types";
import KPIGridArea from "design/components/grids/MeasureGridArea";
import InitiativeDrillDown from "design/components/popups/InitiativeDrillDown";
import KPIDrillDown from "design/components/popups/KPIDrillDown";
import ExecutiveDashboardTitle from "design/components/texts/ExecutiveDashboardTitle";
import LeftMenuPosition from "design/layouts/LeftMenuPosition";
import SplitView from "design/layouts/SplitView";
import StackView from "design/layouts/StackView";
import Page from "design/pages/Page";
import { t } from "i18next";
import { getTranslatedAttribute } from "qpr-base//tplutils/utils";
import * as conf from "qpr-base/configuration";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import useLink from "qpr-base/hooks/useLink";
import Anchor from "qpr-base/ui/Anchor";
import PageContentMenu, {
  PageInternalLink,
} from "qpr-base/uicomponents/view/PageContentMenu";
import React from "react";

const ExecutiveDashboard = () => {
  const ctx = useAppContext();
  const link = useLink();

  const { data: theme } = useScorecardTopElement("L0");
  const { data: el } = useElement(theme?.id);
  const { data, isLoading: isThemesLoading } = useHierarchy(el?.id);

  const [showKpiModal, setShowKPIModal] =
    React.useState<IChartSelectEvent | null>(null);
  const [showInitiatives, setShowInitiatives] =
    React.useState<IChartSelectEvent | null>(null);

  const dashboardKpis = React.useMemo(() => {
    if (!Array.isArray(data)) return [];
    return flattenHierarchy(data).filter(
      (elem) => elem.typename === "KPI"
    );
  }, [data]);

  const corporateKpis = React.useMemo(() => {
    if (!Array.isArray(data)) return [];
    return flattenHierarchy(data).filter(
      (elem) => elem.typename === "KPI" && elem.STATUS === "1"
    );
  }, [data]);
  const dashboardInitiatives = React.useMemo(() => {
    if (!Array.isArray(data)) return [];
    return flattenHierarchy(data).filter(
      (elem) => elem.typename === "Initiative" && elem.STATUS === "1"
    );
  }, [data]);

  const linkItems: PageInternalLink[] = [];
  if (
    conf.getExecutiveDashboardViewSettings().hideDashboardPerformance !== true
  )
    linkItems.push({
      title: t("kpis.Dashboard Performance"),
      anchor: "header",
    });
  if (conf.getExecutiveDashboardViewSettings().hideDashboardKPIs !== true)
    linkItems.push({
      title: t("kpis.Dashboard KPIs"),
      anchor: "dashboard_kpis",
    });
  linkItems.push({ title: t("kpis.Corporate KPIs"), anchor: "corporate_kpis" });
  linkItems.push({
    title: t("initiatives.Corporate Initiatives"),
    anchor: "corporate_initiatives",
  });

  return (
    <Page
      isLoading={isThemesLoading || !el || !data}
      Toolbar={ExecutiveDashboardToolbar}
    >
      {showKpiModal?.category}

      <KPIDrillDown
        title={
          showKpiModal?.name === "No Value"
            ? t("kpis.KPIs with no data")
            : `${showKpiModal?.name} ${t("kpis.performing KPIs")}`
        }
        showModal={showKpiModal}
        onClose={() => {
          setShowKPIModal(null);
        }}
        items={dashboardKpis}
      />

      <InitiativeDrillDown
        title={
          showInitiatives?.name === "No Value"
            ? t("initiatives.Inititiaves with no data")
            : `${showInitiatives?.name} ${t(
                "initiatives.performing Initiatives"
              )}`
        }
        showModal={showInitiatives}
        onClose={() => {
          setShowInitiatives(null);
        }}
      />

      <SplitView left="150px" right="1200px" gap="40px">
        <div>
          <LeftMenuPosition>
            <PageContentMenu
              componentData={{
                items: linkItems,
              }}
            />
          </LeftMenuPosition>
        </div>
        <div>
          <StackView>
            {conf.getExecutiveDashboardViewSettings()
              .hideDashboardPerformance === true ? null : (
              <>
                <Anchor name="header" />

                <ExecutiveDashboardTitle>
                  {t("kpis.Dashboard Performance YTD")} {el?.typename}
                </ExecutiveDashboardTitle>

                <SplitView partCnt={3} gap="35px">
                  <OverallPerformanceCard
                    key="PERF_W_YTD"
                    titleKey="PERF_W_YTD_prettyvalue"
                    element={el!}
                    title={t("overview.Overall Performance")}
                    periodName={ctx.period || ""}
                  />
                  <ElementOverviewDonutCard
                    title={t("kpis.KPI Overview")}
                    data={dashboardKpis!}
                    typenames={["KPI"]}
                    periodName={ctx.period || ""}
                    onSelect={(event) => {
                      setShowKPIModal(event);
                    }}
                  />
                  <ElementOverviewDonutCard
                    title={t("initiatives.Initiative Overview")}
                    data={dashboardInitiatives}
                    typenames={["Initiative"]}
                    periodName={ctx.period || ""}
                    onSelect={(event) => {
                      setShowInitiatives(event);
                    }}
                  />
                </SplitView>
              </>
            )}

            {conf.getExecutiveDashboardViewSettings().hideDashboardKPIs ===
            true ? null : (
              <>
                <Anchor name="dashboard_kpis" />
                <KPIGridArea>
                  <ExecutiveDashboardTitle>
                    {t("kpis.Dashboard KPIs")}
                  </ExecutiveDashboardTitle>
                  {data
                    ? dashboardKpis.map((el) => (
                        <KPICard
                          settings={conf.getExecutiveDashboardViewGaugeSettings(
                            el
                          )}
                          width="285px"
                          height="280px"
                          period={ctx.period || ""}
                          key={`${el.id}-${ctx.period}`}
                          element={el}
                          title={t("configtypes.Score (YTD)")}
                          onSelect={() => {
                            link({ page: "kpidetails", meaId: el.id });
                          }}
                        />
                      ))
                    : null}
                </KPIGridArea>
              </>
            )}

            <Anchor name="corporate_kpis" />
            <KPIGridArea>
              <ExecutiveDashboardTitle>
                {t("kpis.Corporate KPIs")}
              </ExecutiveDashboardTitle>
              {data
                ? corporateKpis.map((el) => (
                    <KPICard
                      settings={conf.getExecutiveDashboardViewGaugeSettings(el)}
                      width="285px"
                      height="280px"
                      period={ctx.period || ""}
                      key={`${el.id}-${ctx.period}`}
                      element={el}
                      title={t("configtypes.Score (YTD)")}
                      onSelect={() => {
                        link({ page: "kpidetails", meaId: el.id });
                      }}
                    />
                  ))
                : null}
            </KPIGridArea>

            <Anchor name="corporate_initiatives" />

            <KPIGridArea>
              <ExecutiveDashboardTitle>
                {t("initiatives.Corporate Initiatives")}
              </ExecutiveDashboardTitle>

              {data
                ? dashboardInitiatives.map((el) => (
                    <InitiativeCard
                      width="280px"
                      height="280px"
                      left_number_key="PLANNED_COMPLETION_prettyvalue"
                      left_number_title={t("initiatives.Planned Progress")}
                      right_number_key="VARIANCE_prettyvalue"
                      right_number_title={t("initiatives.Variance")}
                      cardTitle={t("initiatives.Actual Progress")}
                      period={ctx.period || ""}
                      key={`${el.id}-${ctx.period}`}
                      element={el}
                      translatedName={getTranslatedAttribute(
                        el,
                        "Name",
                        ctx.language
                      )}
                      onSelect={() => {
                        link({ page: "initiativedetails", meaId: el.id });
                      }}
                    />
                  ))
                : null}
            </KPIGridArea>
          </StackView>
        </div>
      </SplitView>
    </Page>
  );
};

export default ExecutiveDashboard;
